import React, { Fragment } from 'react';
import useSWR, { mutate } from 'swr';

import Select from '../Select';
import { translate, Translate } from 'react-i18nify';
import { saveAs } from 'file-saver';
import { connect } from 'react-redux';
import DropdownIndicator from '../DropdownIndicator';
import Button from '../Button';
import api from '../../api/apiClient';
import { HTTP_STATUS_CODES } from '../../constants';
import CanPerform from '../CanPerform';
import Card from '../Card';
import {
  setInvoiceReportMonth,
  setInvoiceReportYear,
  getInvoiceReportRequest,
  getInvoiceReportSuccess,
  getInvoiceReportError
} from '../../actions';

type PriveraFile = {
  fileId: string;
  lastModified: string;
  status: 'new' | 'invoiced';
  yearMonth: string;
  progress: string;
};

type PriveraResponse = {
  yearMonth: string;
  files: PriveraFile[];
};

const InvoiceReport = ({
  authToken,
  reports,
  setInvoiceReportMonth,
  setInvoiceReportYear,
  getInvoiceReportRequest,
  getInvoiceReportSuccess,
  getInvoiceReportError
}) => {
  const { data: priveraFiles, isLoading } = useSWR<PriveraResponse[]>(`/admin/privera`);

  if (isLoading || !priveraFiles) {
    return null;
  }

  const completeReport = (yearMonth: string, fileId: string) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/pdf';

    input.onchange = (e: Event) => {
      const target = e.target as HTMLInputElement;
      const file = target.files?.[0];

      if (file) {
        getInvoiceReportRequest();

        api
          .completePriveraReport(authToken, yearMonth, fileId, file)
          .then((response) => {
            getInvoiceReportSuccess(response);
            mutate('/admin/privera');
          })
          .catch((error) => {
            console.log(error);
            getInvoiceReportError(error);
          });
      }
    };

    input.click();
  };

  const downloadFiles = (yearMonth: string, status: PriveraFile['status'], fileId: string) => {
    api
      .downloadPriveraFiles(authToken, yearMonth, status, fileId)
      .then((response) => {
        saveAs(response, `fakturering_${yearMonth}_${fileId}.zip`);
      })
      .catch((error) => {
        console.log(error);
        getInvoiceReportError(error);
      });
  };

  return priveraFiles.map((group) => (
    <Card
      key={group.yearMonth}
      title={translate('reports_view.invoice_report.header', {
        yearMonth: group.yearMonth
      })}
    >
      {group.files.map((file) => (
        <div key={file.fileId} className="vertical-align space-between">
          <span className="column is-4 space-between">
            <strong>
              <Translate value={`reports_view.invoice_report.${file.status}`} />
            </strong>
            <span>{file.fileId}</span>
          </span>
          <span>
            {file.progress === '100%' ? (
              <Fragment>
                {file.status === 'new' && (
                  <Button onClick={() => completeReport(file.yearMonth, file.fileId)} className="mr-10">
                    <Translate value="reports_view.invoice_report.complete" />
                  </Button>
                )}
                <CanPerform action="readInvoiceReport">
                  {file.status === 'new' && (
                    <Button
                      buttonType="text"
                      onClick={() => downloadFiles(file.yearMonth, file.status, file.fileId)}
                      className="mr-10"
                    >
                      <Translate value="reports_view.invoice_report.download" />
                    </Button>
                  )}
                </CanPerform>

                <CanPerform action="manageBilling">
                  {file.status === 'invoiced' && (
                    <Button
                      buttonType="text"
                      onClick={() => downloadFiles(file.yearMonth, file.status, file.fileId)}
                      className="mr-10"
                    >
                      <Translate value="reports_view.invoice_report.download" />
                    </Button>
                  )}
                </CanPerform>
              </Fragment>
            ) : (
              <span>{file.progress}</span>
            )}
          </span>
        </div>
      ))}
    </Card>
  ));
};

const mapStateToProps = (state) => {
  return {
    authToken: state.auth.token.jwt,
    reports: state.reports
  };
};

const mapActionsToProps = {
  setInvoiceReportMonth,
  setInvoiceReportYear,
  getInvoiceReportRequest,
  getInvoiceReportSuccess,
  getInvoiceReportError
};

export default connect(mapStateToProps, mapActionsToProps)(InvoiceReport);
